/**
 * Hub React Query Hooks
 *
 * Provides hooks for managing conversations, messages, and contacts.
 * Replaces the server-data portion of the hub Zustand store.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';
import { queryClient } from './queryClient';

// =============================================================================
// TYPES (imported from domain/models, not re-exported to avoid conflicts)
// =============================================================================

import type {
  Conversation,
  MessageItem,
  Contact,
  ContactHandles,
  Channel,
  Direction,
} from '../domain/models';

// =============================================================================
// GRAPHQL QUERIES
// =============================================================================

const GET_CONTACTS_QUERY = `
  query GetContacts {
    contacts {
      id
      displayName
      company
      role
      notes
      tags
      handles {
        email
        phone
        whatsapp
        telegram
      }
      createdAt
      updatedAt
    }
  }
`;

const GET_CONVERSATIONS_QUERY = `
  query GetConversations {
    conversations {
      id
      channel
      title
      lastMessageAt
      contactId
      contactName
      unreadCount
      projectId
      updatedAt
    }
  }
`;

const GET_CONVERSATION_MESSAGES_QUERY = `
  query GetConversationMessages($conversationId: String!) {
    conversationMessages(conversationId: $conversationId) {
      id
      conversationId
      timestamp
      content
      direction
      isRead
    }
  }
`;

// =============================================================================
// GRAPHQL MUTATIONS
// =============================================================================

const CREATE_CONVERSATION_MUTATION = `
  mutation CreateConversation($input: ConversationInput!) {
    createConversation(input: $input) {
      id
      channel
      title
      lastMessageAt
      contactId
      contactName
      unreadCount
      projectId
      updatedAt
    }
  }
`;

const UPDATE_CONVERSATION_MUTATION = `
  mutation UpdateConversation($id: ID!, $input: ConversationInput!) {
    updateConversation(id: $id, input: $input) {
      id
      channel
      title
      lastMessageAt
      contactId
      contactName
      unreadCount
      projectId
      updatedAt
    }
  }
`;

const DELETE_CONVERSATION_MUTATION = `
  mutation DeleteConversation($id: ID!) {
    deleteConversation(id: $id)
  }
`;

const MARK_CONVERSATION_AS_READ_MUTATION = `
  mutation MarkConversationAsRead($conversationId: String!) {
    markConversationAsRead(conversationId: $conversationId)
  }
`;

const ADD_MESSAGE_MUTATION = `
  mutation AddMessage($input: MessageInput!) {
    addMessage(input: $input) {
      id
      conversationId
      timestamp
      content
      direction
      isRead
    }
  }
`;

const CREATE_CONTACT_MUTATION = `
  mutation CreateContact($input: ContactInput!) {
    createContact(input: $input) {
      id
      displayName
      company
      role
      notes
      tags
      handles {
        email
        phone
        whatsapp
        telegram
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_CONTACT_MUTATION = `
  mutation UpdateContact($id: ID!, $input: ContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      displayName
      company
      role
      notes
      tags
      handles {
        email
        phone
        whatsapp
        telegram
      }
      createdAt
      updatedAt
    }
  }
`;

const DELETE_CONTACT_MUTATION = `
  mutation DeleteContact($id: ID!) {
    deleteContact(id: $id)
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

/**
 * Map server contact data to local Contact type
 */
function mapServerContact(serverContact: any): Contact {
  return {
    id: serverContact.id,
    displayName: serverContact.displayName,
    handles: serverContact.handles || {},
    company: serverContact.company,
    role: serverContact.role,
    notes: serverContact.notes,
    tags: serverContact.tags || [],
    createdAt: serverContact.createdAt,
    updatedAt: serverContact.updatedAt,
  };
}

/**
 * Map server conversation data to local Conversation type
 */
function mapServerConversation(serverConversation: any): Conversation {
  return {
    id: serverConversation.id,
    channel: (serverConversation.channel || 'email') as Channel,
    title: serverConversation.title,
    lastAt: serverConversation.lastMessageAt || serverConversation.updatedAt,
    contactId: serverConversation.contactId,
    contactName: serverConversation.contactName,
    unread: serverConversation.unreadCount,
    projectId: serverConversation.projectId,
  };
}

/**
 * Map server message data to local MessageItem type
 */
function mapServerMessage(serverMessage: any): MessageItem {
  return {
    id: serverMessage.id,
    conversationId: serverMessage.conversationId,
    at: serverMessage.timestamp,
    text: serverMessage.content,
    direction: (serverMessage.direction || 'out') as Direction,
    read: serverMessage.isRead,
  };
}

/**
 * Map local contact to GraphQL input (snake_case)
 */
function contactToInput(contact: Partial<Contact>) {
  return {
    display_name: contact.displayName,
    company: contact.company || null,
    role: contact.role || null,
    notes: contact.notes || null,
    tags: contact.tags || [],
    handles: contact.handles
      ? {
          email: contact.handles.email || null,
          phone: contact.handles.phone || null,
          whatsapp: contact.handles.whatsapp || null,
          telegram: contact.handles.telegram || null,
        }
      : null,
  };
}

/**
 * Map local conversation to GraphQL input (snake_case)
 */
function conversationToInput(conversation: Partial<Conversation>) {
  return {
    channel: conversation.channel,
    title: conversation.title,
    contact_id: conversation.contactId || null,
    contact_name: conversation.contactName || null,
    unread_count: conversation.unread || 0,
    project_id: conversation.projectId || null,
  };
}

/**
 * Map local message to GraphQL input (snake_case)
 */
function messageToInput(message: {
  conversationId: string;
  text?: string;
  direction: Direction;
  read?: boolean;
}) {
  return {
    conversation_id: message.conversationId,
    content: message.text || '',
    direction: message.direction,
    is_read: message.read || false,
  };
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all contacts from the server.
 * Stale time: 5 minutes
 */
export function useContacts() {
  return useQuery({
    queryKey: queryKeys.hub.contacts(),
    queryFn: async () => {
      const response = await graphqlFetch<{ contacts: any[] }>(GET_CONTACTS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.contacts || []).map(mapServerContact);
    },
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Fetch all conversations from the server.
 * Stale time: 1 minute (real-time data)
 */
export function useConversations() {
  return useQuery({
    queryKey: queryKeys.hub.conversations(),
    queryFn: async () => {
      const response = await graphqlFetch<{ conversations: any[] }>(GET_CONVERSATIONS_QUERY);
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.conversations || []).map(mapServerConversation);
    },
    staleTime: 1 * 60_000, // 1 minute
  });
}

/**
 * Fetch messages for a specific conversation.
 * Stale time: 30 seconds
 * Only enabled when conversationId is truthy.
 */
export function useConversationMessages(conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.hub.messages(conversationId || ''),
    queryFn: async () => {
      if (!conversationId) return [];
      const response = await graphqlFetch<{ conversationMessages: any[] }>(
        GET_CONVERSATION_MESSAGES_QUERY,
        { conversationId }
      );
      if (response.errors) {
        console.error('GraphQL errors:', response.errors);
        return [];
      }
      return (response.data?.conversationMessages || []).map(mapServerMessage);
    },
    staleTime: 30_000, // 30 seconds
    enabled: !!conversationId,
  });
}

// =============================================================================
// MUTATION HOOKS - CONVERSATIONS
// =============================================================================

/**
 * Create a new conversation.
 */
export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      conversation: Omit<Conversation, 'id' | 'lastAt'>
    ) => {
      const input = conversationToInput(conversation);
      const response = await graphqlFetch<{ createConversation: any }>(
        CREATE_CONVERSATION_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createConversation) {
        return mapServerConversation(response.data.createConversation);
      }
      throw new Error('Failed to create conversation');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.conversations() });
    },
  });
}

/**
 * Update an existing conversation.
 */
export function useUpdateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Conversation>;
    }) => {
      const input = conversationToInput(updates);
      const response = await graphqlFetch<{ updateConversation: any }>(
        UPDATE_CONVERSATION_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateConversation) {
        return mapServerConversation(response.data.updateConversation);
      }
      throw new Error('Failed to update conversation');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.conversations() });
    },
  });
}

/**
 * Delete a conversation.
 */
export function useDeleteConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteConversation: boolean }>(
        DELETE_CONVERSATION_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteConversation) {
        throw new Error('Failed to delete conversation');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.conversations() });
    },
  });
}

/**
 * Mark a conversation as read (set unread count to 0).
 */
export function useMarkConversationAsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const response = await graphqlFetch<{ markConversationAsRead: boolean }>(
        MARK_CONVERSATION_AS_READ_MUTATION,
        { conversationId }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.markConversationAsRead) {
        throw new Error('Failed to mark conversation as read');
      }
      return true;
    },
    onSuccess: (_, conversationId) => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.conversations() });
      qc.invalidateQueries({ queryKey: queryKeys.hub.messages(conversationId) });
    },
  });
}

// =============================================================================
// MUTATION HOOKS - MESSAGES
// =============================================================================

/**
 * Add a new message to a conversation.
 */
export function useAddMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationId,
      text,
      direction,
      read,
    }: {
      conversationId: string;
      text?: string;
      direction: Direction;
      read?: boolean;
    }) => {
      const input = messageToInput({ conversationId, text, direction, read });
      const response = await graphqlFetch<{ addMessage: any }>(
        ADD_MESSAGE_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.addMessage) {
        return mapServerMessage(response.data.addMessage);
      }
      throw new Error('Failed to add message');
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.messages(variables.conversationId) });
      qc.invalidateQueries({ queryKey: queryKeys.hub.conversations() });
    },
  });
}

// =============================================================================
// MUTATION HOOKS - CONTACTS
// =============================================================================

/**
 * Create a new contact.
 */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      const input = contactToInput(contact);
      const response = await graphqlFetch<{ createContact: any }>(
        CREATE_CONTACT_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.createContact) {
        return mapServerContact(response.data.createContact);
      }
      throw new Error('Failed to create contact');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.contacts() });
    },
  });
}

/**
 * Update an existing contact.
 */
export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Contact>;
    }) => {
      const input = contactToInput(updates);
      const response = await graphqlFetch<{ updateContact: any }>(
        UPDATE_CONTACT_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (response.data?.updateContact) {
        return mapServerContact(response.data.updateContact);
      }
      throw new Error('Failed to update contact');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.contacts() });
    },
  });
}

/**
 * Delete a contact.
 */
export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteContact: boolean }>(
        DELETE_CONTACT_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.deleteContact) {
        throw new Error('Failed to delete contact');
      }
      return true;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.contacts() });
    },
  });
}

// =============================================================================
// HELPER FUNCTIONS (Pure functions for filtering/selecting)
// =============================================================================

/**
 * Filter conversations by a filter key.
 * Supports: all, unread, recent, channels, project filters
 */
export function filterConversations(
  conversations: Conversation[],
  filterKey: string,
  hoursForRecent: number = 24
): Conversation[] {
  switch (filterKey) {
    case 'all':
      return conversations;
    case 'unread':
      return conversations.filter((c) => (c.unread || 0) > 0);
    case 'recent': {
      const cutoff = new Date(Date.now() - hoursForRecent * 60 * 60 * 1000).toISOString();
      return conversations.filter((c) => c.lastAt >= cutoff);
    }
    case 'email':
    case 'whatsapp':
    case 'telegram':
    case 'call':
    case 'videocall':
    case 'sms':
      return conversations.filter((c) => c.channel === filterKey);
    case 'with-project':
      return conversations.filter((c) => !!c.projectId);
    case 'without-project':
      return conversations.filter((c) => !c.projectId);
    default:
      return conversations;
  }
}

/**
 * Get conversations by specific channel.
 */
export function getConversationsByChannel(
  conversations: Conversation[],
  channel: Channel
): Conversation[] {
  return conversations.filter((c) => c.channel === channel);
}

/**
 * Get all unread conversations.
 */
export function getUnreadConversations(conversations: Conversation[]): Conversation[] {
  return conversations.filter((c) => (c.unread || 0) > 0);
}

/**
 * Get recent conversations (last N hours).
 */
export function getRecentConversations(
  conversations: Conversation[],
  hours: number = 24
): Conversation[] {
  const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
  return conversations.filter((c) => c.lastAt >= cutoff);
}

/**
 * Find a contact by handle (email, phone, whatsapp, telegram).
 * Case-insensitive for email/telegram.
 */
export function findContactByHandle(contacts: Contact[], handle: string): Contact | null {
  const normalizedHandle = handle.toLowerCase();
  return (
    contacts.find((c) => {
      const handles = c.handles;
      return (
        handles.email?.toLowerCase() === normalizedHandle ||
        handles.phone === handle ||
        handles.whatsapp === handle ||
        handles.telegram?.toLowerCase() === normalizedHandle
      );
    }) || null
  );
}

/**
 * Sort messages by timestamp (ascending).
 */
export function sortMessagesByTime(messages: MessageItem[]): MessageItem[] {
  return [...messages].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
  );
}

// =============================================================================
// NON-REACT CACHE ACCESS
// =============================================================================

/**
 * Get conversations from the cache (non-React context).
 */
export function getConversationsFromCache(): Conversation[] {
  const data = queryClient.getQueryData<Conversation[]>(queryKeys.hub.conversations());
  return data || [];
}

/**
 * Get contacts from the cache (non-React context).
 */
export function getContactsFromCache(): Contact[] {
  const data = queryClient.getQueryData<Contact[]>(queryKeys.hub.contacts());
  return data || [];
}

/**
 * Get messages for a conversation from the cache (non-React context).
 */
export function getMessagesFromCache(conversationId: string): MessageItem[] {
  const data = queryClient.getQueryData<MessageItem[]>(queryKeys.hub.messages(conversationId));
  return data || [];
}

// =============================================================================
// COMPANIES (backend: companies / createCompany(name))
// =============================================================================

export interface Company {
  id: string;
  name: string;
}

const GET_COMPANIES_QUERY = `
  query GetCompanies {
    companies {
      id
      name
    }
  }
`;

const CREATE_COMPANY_MUTATION = `
  mutation CreateCompany($name: String!) {
    createCompany(name: $name) {
      id
      name
    }
  }
`;

/**
 * Fetch all companies (clients) from the server.
 */
export function useCompanies() {
  return useQuery({
    queryKey: queryKeys.hub.companies(),
    queryFn: async (): Promise<Company[]> => {
      const response = await graphqlFetch<{ companies: Company[] }>(GET_COMPANIES_QUERY);
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      return response.data?.companies ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Create a company (client) and refresh the companies list.
 */
export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string): Promise<Company> => {
      const response = await graphqlFetch<{ createCompany: Company | null }>(
        CREATE_COMPANY_MUTATION,
        { name: name.trim() }
      );
      if (response.errors) {
        throw new Error(response.errors.map((e) => e.message).join(', '));
      }
      if (!response.data?.createCompany) {
        throw new Error('Failed to create company');
      }
      return response.data.createCompany;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.hub.companies() });
    },
  });
}
